import { FC, ReactElement, memo, useCallback } from 'react';
import { useIsMutating, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { DomainRegistrarAccountsQueryKeys, DomainRegistrarsQueryKeys, useDeleteDomainRegistrarsMutation, useGetOneByIdDomainRegistrarsQuery } from '@/modules/DomainRegistrars';

import { DomainRegistrarsOverviewView } from './DomainRegistrarsOverviewView';

interface Props {
  domainRegistrarId: string;
}

export const DomainRegistrarsOverviewContainer: FC<Props> = memo(({ domainRegistrarId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: domainRegistrarData,
    error: domainRegistrarError,
    isFetching: isDomainRegistrarFetching,
    isError: isDomainRegistrarError
  } = useGetOneByIdDomainRegistrarsQuery(domainRegistrarId)({
    enabled: !!domainRegistrarId
  });

  const { isLoading: isDeleteDomainRegistrtarLoading, mutate: deleteDomainRegistrtarMutation } =
    useDeleteDomainRegistrarsMutation(domainRegistrarId)({
      onSuccess: async () => {
        appMessageApi.success('Проксі-сервер успішно видалено!');
        navigate(AppRoutes.DomainRegistrars);
        await queryClient.refetchQueries([DomainRegistrarsQueryKeys.GetAll]);
      },
      onError: (error) => {
        messageOnError(error, appMessageApi);
      }
    });

  const isDomainRegistrarAccountsCreating = !!useIsMutating({
    mutationKey: [DomainRegistrarAccountsQueryKeys.Create, domainRegistrarId],
    exact: true
  });

  const isDomainRegistrarAccountsEditing = !!useIsMutating({
    mutationKey: [DomainRegistrarAccountsQueryKeys.Edit, domainRegistrarId],
    exact: true
  });

  const isDomainRegistrarAccountsDeleting = !!useIsMutating({
    mutationKey: [DomainRegistrarAccountsQueryKeys.Delete, domainRegistrarId],
    exact: true
  });

  const handleDeleteDomainRegistrtar = useCallback((): void => {
    deleteDomainRegistrtarMutation();
  }, []);

  return (
    <FallbackedLayout
      data={ domainRegistrarData }
      isLoading={ isDomainRegistrarFetching }
      isError={ isDomainRegistrarError }
      error={ domainRegistrarError }
    >
      { (domainRegistrar): ReactElement => (
        <DomainRegistrarsOverviewView
          domainRegistrar={ domainRegistrar }
          isDeleteDomainRegistrtarLoading={ isDeleteDomainRegistrtarLoading }
          isDomainRegistrarAccountsListMutating={
            isDomainRegistrarAccountsCreating || isDomainRegistrarAccountsEditing || isDomainRegistrarAccountsDeleting
          }
          handleDeleteDomainRegistrtar={ handleDeleteDomainRegistrtar }
        />
      ) }
    </FallbackedLayout>
  );
});
